import { useState, useEffect, useCallback } from 'react';
import { serviceCatalogAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Plus, Pencil, Trash2, IndianRupee, Tag, Clock, FileText } from 'lucide-react';
import { cn } from '../lib/utils';

const CATEGORIES = ['Consultation', 'Procedure', 'Surgery', 'Lab Test', 'Imaging', 'Room Charge', 'Pharmacy', 'Emergency', 'Other'];

const ServiceCatalogPage = () => {
  const { hospital } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const hospitalId = hospital?.id;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (hospitalId) params.hospital_id = hospitalId;
      if (filterCat !== 'all') params.category = filterCat;
      const res = await serviceCatalogAPI.list(params);
      setServices(res.data || []);
    } catch { toast.error('Failed to load services'); }
    setLoading(false);
  }, [hospitalId, filterCat]);

  useEffect(() => { load(); }, [load]);

  const filtered = services.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.billing_code?.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (id) => {
    if (!confirm('Delete this service?')) return;
    try { await serviceCatalogAPI.delete(id); toast.success('Service deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const catCounts = {};
  services.forEach(s => { catCounts[s.category] = (catCounts[s.category] || 0) + 1; });

  return (
    <div className="space-y-5" data-testid="service-catalog-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Service Catalog</h1>
          <p className="text-sm text-slate-500 mt-0.5">{services.length} services configured</p>
        </div>
        <Button onClick={() => { setEditItem(null); setShowModal(true); }} className="bg-teal-600 hover:bg-teal-700" data-testid="add-service-btn">
          <Plus size={16} className="mr-1.5" /> Add Service
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {CATEGORIES.filter(c => catCounts[c]).map(cat => (
          <button key={cat} onClick={() => setFilterCat(filterCat === cat ? 'all' : cat)}
            className={cn('rounded-xl border p-3 text-left transition-all',
              filterCat === cat ? 'border-teal-500 bg-teal-50' : 'border-slate-200 bg-white hover:bg-slate-50')}>
            <p className="text-lg font-bold text-slate-800">{catCounts[cat]}</p>
            <p className="text-xs text-slate-500 truncate">{cat}</p>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search services or billing codes..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-10 h-10" data-testid="search-services" />
          </div>
          {filterCat !== 'all' && (
            <Button variant="outline" size="sm" onClick={() => setFilterCat('all')}>Clear Filter</Button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Service Name</th>
                <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Category</th>
                <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Code</th>
                <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Price</th>
                <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Tax %</th>
                <th className="text-center py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Duration</th>
                <th className="text-right py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-slate-400">Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-slate-400">No services found. Add your first service.</td></tr>
              ) : filtered.map(svc => (
                <tr key={svc.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`service-row-${svc.id}`}>
                  <td className="py-3 px-3">
                    <p className="font-medium text-slate-800">{svc.name}</p>
                    {svc.description && <p className="text-xs text-slate-400 mt-0.5 truncate max-w-xs">{svc.description}</p>}
                  </td>
                  <td className="py-3 px-3">
                    <span className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-medium">{svc.category}</span>
                  </td>
                  <td className="py-3 px-3 font-mono text-xs text-slate-500">{svc.billing_code || '—'}</td>
                  <td className="py-3 px-3 text-right font-semibold text-slate-800">
                    <span className="flex items-center justify-end gap-0.5"><IndianRupee size={12} />{svc.price?.toLocaleString()}</span>
                  </td>
                  <td className="py-3 px-3 text-right text-slate-500">{svc.tax_rate || 0}%</td>
                  <td className="py-3 px-3 text-center text-slate-500">{svc.duration_minutes ? `${svc.duration_minutes}m` : '—'}</td>
                  <td className="py-3 px-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => { setEditItem(svc); setShowModal(true); }} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid={`edit-${svc.id}`}>
                        <Pencil size={14} className="text-slate-400" />
                      </button>
                      <button onClick={() => handleDelete(svc.id)} className="p-1.5 rounded-lg hover:bg-red-50" data-testid={`delete-${svc.id}`}>
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ServiceModal open={showModal} onClose={() => setShowModal(false)} editItem={editItem} hospitalId={hospitalId} onSaved={load} />
    </div>
  );
};

const ServiceModal = ({ open, onClose, editItem, hospitalId, onSaved }) => {
  const [form, setForm] = useState({ name: '', category: 'Consultation', description: '', price: '', tax_rate: '', billing_code: '', duration_minutes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editItem) {
      setForm({
        name: editItem.name || '', category: editItem.category || 'Consultation', description: editItem.description || '',
        price: editItem.price?.toString() || '', tax_rate: editItem.tax_rate?.toString() || '', billing_code: editItem.billing_code || '',
        duration_minutes: editItem.duration_minutes?.toString() || '',
      });
    } else {
      setForm({ name: '', category: 'Consultation', description: '', price: '', tax_rate: '', billing_code: '', duration_minutes: '' });
    }
  }, [editItem, open]);

  const handleSave = async () => {
    if (!form.name) { toast.error('Service name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) || 0, tax_rate: parseFloat(form.tax_rate) || 0, duration_minutes: parseInt(form.duration_minutes) || 0, hospital_id: hospitalId };
      if (editItem) await serviceCatalogAPI.update(editItem.id, payload);
      else await serviceCatalogAPI.create(payload);
      toast.success(editItem ? 'Service updated' : 'Service created');
      onSaved(); onClose();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{editItem ? 'Edit Service' : 'Add New Service'}</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Service Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. General Consultation" data-testid="service-name-input" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="service-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Billing Code</Label>
              <Input value={form.billing_code} onChange={e => setForm({ ...form, billing_code: e.target.value })} placeholder="e.g. CON-001" />
            </div>
            <div>
              <Label>Price (INR)</Label>
              <Input type="number" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} placeholder="500" data-testid="service-price-input" />
            </div>
            <div>
              <Label>Tax Rate (%)</Label>
              <Input type="number" value={form.tax_rate} onChange={e => setForm({ ...form, tax_rate: e.target.value })} placeholder="18" />
            </div>
            <div>
              <Label>Duration (minutes)</Label>
              <Input type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: e.target.value })} placeholder="30" />
            </div>
            <div className="col-span-2">
              <Label>Description</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Brief description of the service" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-service-btn">
              {saving ? 'Saving...' : editItem ? 'Update' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceCatalogPage;
